import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"

// The offset in pixels from the bottom of the viewport.
const OFFSET = 100

/**
 * This comonent calls the onView callback when it is lazily rendered.
 */
export function LazyWrapper({ children, onView }) {
  useEffect(() => {
    if (onView) {
      onView()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return children
}

/**
 * This component wraps its children to make them lazily loadable, deferring rendering
 * until they scroll within OFFSET pixels of the viewport.
 */
function LazyLoad({ children, onView }) {
  const [element, setElement] = useState(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    // Fail safe on browsers without IntersectionObserver by rendering eagerly.
    // https://caniuse.com/intersectionobserver
    if (typeof IntersectionObserver === "undefined") {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setVisible(true)
      return
    }

    if (!element) {
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          observer.disconnect()
        }
      },
      { rootMargin: `${OFFSET}px` },
    )
    observer.observe(element)

    return () => observer.disconnect()
  }, [element])

  if (!visible) {
    // A zero-height sentinel can fail to intersect in some browsers (e.g. Firefox at
    // the bottom of the document), so give it a real, if minimal, height.
    return <div ref={setElement} style={{ height: 1 }} />
  }

  return <LazyWrapper onView={onView}>{children}</LazyWrapper>
}

LazyLoad.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  onView: PropTypes.func.isRequired,
}

export default LazyLoad
